import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'wouter';

export default function Terms() {
  const [language, setLanguage] = useState<'is' | 'en'>('is');

  // Load language preference from localStorage on mount
  useEffect(() => {
    const savedLang = localStorage.getItem('termsLanguage') as 'is' | 'en' | null;
    if (savedLang) {
      setLanguage(savedLang);
    }
  }, []);

  // Save language preference to localStorage when it changes
  const handleLanguageChange = (lang: 'is' | 'en') => {
    setLanguage(lang);
    localStorage.setItem('termsLanguage', lang);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto max-w-4xl px-4 py-8">
        <Link href="/login" className="mb-6 inline-flex items-center text-primary hover:underline">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {language === 'is' ? 'Til baka' : 'Back'}
        </Link>

        <div className="rounded-lg border border-border bg-card p-8 shadow-lg">
          {/* Language Toggle */}
          <div className="mb-4 flex justify-end gap-2">
            <Button
              variant={language === 'is' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleLanguageChange('is')}
            >
              🇮🇸 IS
            </Button>
            <Button
              variant={language === 'en' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleLanguageChange('en')}
            >
              🇬🇧 EN
            </Button>
          </div>

          {language === 'is' ? (
            // Icelandic Version
            <>
              <h1 className="mb-6 text-center font-bold text-3xl">Skilmálar og persónuverndarstefna</h1>
              <h2 className="mb-8 text-center font-bold text-2xl">Skilmálar fyrir notkun Skríbunnar</h2>

              <div className="space-y-6 text-foreground">
                <div>
                  <p>
                    Þessi skilmálar um notkun ("Skilmálar") gilda um aðgang þinn að og notkun á hugbúnaðarþjónusta sem
                    hljóðritar og býr til sjálfvirkar samantektir á klínískum viðtölum og er veitt af{' '}
                    <strong>Careflux ehf.</strong>, kt. 490224-1140, að Menntavegi 1, 102 Reykjavík, sem og öllum
                    tilheyrandi þjónustum og kerfum sem saman kallast „Skríban" eða „þjónustan".
                  </p>
                  <p className="mt-4">
                    Með því að nota þjónustuna samþykkir þú þessa skilmála og skuldbindur þig til að fylgja þeim.
                  </p>
                </div>

                <div>
                  <h3 className="mb-3 font-bold text-xl">1. Markmið þjónustunnar</h3>
                  <p>
                    Skríban er hugbúnaður sem tekur upp samtal heilbrigðisstafsfólks og skjólstæðinga og býr sjálfvirkt
                    til samantektir og nótur byggt á því. Skríban er einnig notuð beint af heilbrigðisstarfsmanni án
                    skjólstæðings til að rita samantekt fyrir eða eftir samtal við skjólstæðing. Þetta gerir
                    heilbrigðisstarfsfólki kleift að einbeita sér betur að skjólstæðingum sínum þegar þeir eru í viðtali
                    en ella. Skríban samanstendur af: (1) gátt í vafra þar sem heilbrigðisstarfsfólk skráir sig inn með
                    öruggum hætti; og (2) hugbúnaði sem notar gervigreindarlíkön til þess að greina efni og innihald
                    samtalana með sjálfvirkum hætti, þ.e. hljóðritar textann og býr til samantektir sem
                    heilbrigðisstarfsmaður les yfir, samþykkir eða breytir eftir þörfum.
                  </p>
                  <p className="mt-4">
                    Þessir notendaskilmálar eiga við um heilbrigðisstarfsfólk sem notar þessa þjónustu til þess að fá
                    hjóðritun, úrvinnslu og samantekt á samtölum við skjólstæðinga.
                  </p>
                  <p className="mt-4">
                    Þjónustan tengist sjúkraskrám með viðurkenndum og öruggum hætti, þar sem vistun gagna fer fram
                    samkvæmt gildandi lögum.
                  </p>
                  <p className="mt-4">
                    <strong>Þjónustan kemur ekki í stað læknisfræðilegrar greiningar eða ráðgjafar.</strong>
                  </p>
                </div>

                <div>
                  <h3 className="mb-3 font-bold text-xl">2. Skilyrði fyrir notkun</h3>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">2.1. Aldur og hæfi</h4>
                  <p>
                    Þjónustan er ætluð heilbrigðisstarfsfólki sem er 18 ára og eldri og starfar hjá viðurkenndum
                    heilbrigðisþjónustuveitanda.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">2.2. Auðkenning og aðgangur</h4>
                  <p>
                    Aðgangur að þjónustunni krefst rafrænnar auðkenningar með rafrænum skilríkjum í samræmi við kröfur
                    þess heilbrigðisveitanda þar sem heilbrigðisstarfsmaðurinn starfar. Slík auðkenning er forsenda þess
                    að tryggja öryggi og lögmæti gagnameðferðar.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">2.3. Réttmæti notkunar</h4>
                  <p>Með því að nota þjónustuna staðfestir þú að:</p>
                  <ul className="mt-2 ml-6 list-disc space-y-1">
                    <li>Þú sért sá einstaklingur sem þú gefur þig út fyrir að vera.</li>
                    <li>
                      Þú notir þjónustuna eingöngu í lögmætum tilgangi og innan þess ramma sem þjónustan er hönnuð
                      fyrir.
                    </li>
                  </ul>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">2.4. Breytingar á aðgengi</h4>
                  <p>Careflux áskilur sér rétt til að:</p>
                  <ul className="mt-2 ml-6 list-disc space-y-1">
                    <li>
                      Takmarka eða loka fyrir aðgang að þjónustunni tímabundið eða varanlega og án fyrirvara, ef grunur
                      leikur á brotum á skilmálum, misnotkun, rangfærslum eða ólögmætri hegðun.
                    </li>
                    <li>Veita eða afturkalla aðgang að þjónustunni að beiðni heilbrigðisveitanda sem notar hana.</li>
                  </ul>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">2.5. Tæknilegar kröfur</h4>
                  <p>
                    Til þess að nota þjónustuna þarf heilbrigðisstarfsmaður að hafa rafræn skilríki, hljóðnema, aðgang
                    að nettengdri tölvu- eða farsímatæki og nýta vefvafra sem styður öryggisstaðla þjónustunnar. Notandi
                    ber ábyrgð á að tryggja að búnaður og nettenging uppfylli þessi skilyrði.
                  </p>
                </div>

                <div>
                  <h3 className="mb-3 font-bold text-xl">3. Notkun þjónustunnar</h3>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">3.1. Tilgangur þjónustunnar</h4>
                  <p>
                    Þjónustan er vettvangur fyrir samskipti einstaklinga og heilbrigðisstarfsfólks þar sem hægt er að
                    taka upp samtöl og fá samantektir á öruggan hátt.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">3.2. Aðföng þjónustunnar</h4>
                  <p>
                    Þjónustan tekur upp samtöl eða dikteringu, vinnur sjálfvirkt úr innihaldi þeirra og myndar svo
                    samantektir fyrir heilbrigðisstarfsfólk. Heilbrigðisstarfsmaður yfirfer þessi gögn og ber ábyrgð á
                    endanlegri ákvörðun og úrvinnslu.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">3.3. Óheimil notkun</h4>
                  <p>Óheimilt er að:</p>
                  <ul className="mt-2 ml-6 list-disc space-y-1">
                    <li>Breyta samantektum með hætti sem er villandi, ósæmilegt eða ólöglegt.</li>
                    <li>Nota þjónustuna til að skaða, ógna, áreita eða blekkja aðra.</li>
                    <li>
                      Senda efni sem brýtur gegn lögum eða réttindum þriðja aðila, s.s. höfundarétti, friðhelgi eða
                      persónuvernd.
                    </li>
                    <li>Nota þjónustuna í vélrænum tilgangi eða í gegnum forrit sem hermir eftir manneskju.</li>
                    <li>
                      Nota þjónustuna til þess að vinna úr samtölum sem gefa til kynna neyðartilvik. Í slíkum tilfellum
                      skal benda viðkomandi að hringja í 112 og bregðast skjótt við með þeim hætti sem mögulegt er
                      hverju sinni.
                    </li>
                  </ul>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">3.4. Afgreiðsla erinda</h4>
                  <p>
                    Starfsfólk Careflux skoðar ekki innihald upptakanna og ber ekki ábyrgð á því hvenær eða hvernig
                    heilbrigðisstarfsfólk vinnur úr þeim. Notkun og úrvinnsla getur ráðist af reglum og verkferlum
                    viðkomandi heilbrigðisstofnunar.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">3.5. Upplýsingagjöf og ábyrgð notanda</h4>
                  <p>
                    Heilbrigðisstarfsfólk sem notar þjónustuna ber ábyrgð á að veita réttar og skýrar upplýsingar. Ef
                    notandi veitir rangar upplýsingar getur það haft áhrif á úrvinnslu samtalanna, valdið villum og
                    töfum eða skaða.
                  </p>
                  <p className="mt-4">
                    Heilbrigðisstarfsfólk sem notar Skríbuna til dikteringar og hefur hakað í "Skrá án
                    persónugreinanlegra upplýsinga" ber að sleppa persónugreinanlegum atriðum í dikteringunni. Þetta á
                    helst við um fullt nafn viðkomandi, kennitölu, heimilisfang, nákvæma starfsstöð.
                  </p>
                  <p className="mt-4">
                    Skríban notar gervigreindarlíkan sem getur gert villur. Við undirritun á þessum skilmálum samþykkir
                    þú að það sé á þinni ábyrgð sem heilbrigðisstarfsmaður að lesa yfir og yfirfara þær upplýsingar sem
                    Skríban leggur til áður en þær eru unnar frekar.
                  </p>
                </div>

                <div>
                  <h3 className="mb-3 font-bold text-xl">4. Persónuvernd og gagnaöryggi</h3>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">4.1. Meðferð persónuupplýsinga</h4>
                  <p>
                    Careflux vinnur persónuupplýsingar í samræmi við lög um persónuvernd og vinnslu persónuupplýsinga
                    nr. 90/2018 og reglugerð (ESB) 2016/679 (GDPR). Upplýsingar sem notendur senda í gegnum þjónustuna
                    geta talist viðkvæmar persónuupplýsingar, til dæmis upplýsingar um heilsufar, og eru meðhöndlaðar
                    samkvæmt ströngustu öryggisstöðlum.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">4.2. Hlutverk og ábyrgð</h4>
                  <p>
                    Careflux starfar sem vinnsluaðili fyrir hönd viðkomandi heilbrigðisstofnunar eða þjónustuveitanda,
                    sem ber ábyrgð á upplýsingunum sem ábyrgðaraðili. Öll vistun og meðferð gagna fer fram í umboði og
                    samkvæmt leiðbeiningum viðkomandi heilbrigðisveitanda.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">4.3. Flutningur gagna</h4>
                  <p>
                    Gögn sem send eru með Skríbunni eru dulkóðuð í flutningi og vistuð í samræmi við öryggiskröfur í
                    skýjalausnum og/eða hjá viðkomandi heilbrigðisþjónustuveitanda. Careflux flytur ekki gögn til þriðja
                    aðila nema með skýru samþykki, lögboðinni skyldu eða samningi við ábyrgðaraðila.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">4.4. Réttindi þín sem skráðs aðila</h4>
                  <p>Sem heilbrigðisstarfsmaður og notandi Skríbunnar hefur þú ákveðin réttindi samkvæmt lögum:</p>
                  <ul className="mt-2 ml-6 list-disc space-y-1">
                    <li>Rétt á aðgangi að söfnuðum upplýsingum.</li>
                    <li>Rétt til leiðréttingar á röngum upplýsingum.</li>
                    <li>Rétt til takmörkunar á vinnslu og að andmæla vinnslu.</li>
                    <li>Rétt til eyðingar á eigin gögnum.</li>
                    <li>Rétt til þess að draga þitt samþykki til baka.</li>
                    <li>Rétt til þess að leggja fram kvörtun til Persónuverndar.</li>
                  </ul>
                  <p className="mt-4">
                    Slíkum beiðnum skal beina til viðkomandi heilbrigðisþjónustuveitanda sem ber ábyrgð á meðferð
                    upplýsinganna eða til persónuverndarfulltrúa Careflux í hello@careflux.ai.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">4.5. Öryggisráðstafanir</h4>
                  <p>
                    Skríban beitir tæknilegum og skipulagslegum öryggisráðstöfunum til að vernda gögn gegn óheimilum
                    aðgangi, glötun, eyðingu, eða óheimilli notkun. Þetta felur m.a. í sér dulkóðun, aðgangsstýringu,
                    öryggisprófanir og reglulegt öryggiseftirlit.
                  </p>
                </div>

                <div>
                  <h3 className="mb-3 font-bold text-xl">5. Ábyrgð Careflux</h3>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">5.1. Tækniþjónustuveitandi</h4>
                  <p>
                    Careflux veitir eingöngu tæknilega lausn með þjónustunni. Careflux veitir ekki læknisfræðilega eða
                    heilbrigðisþjónustu, ráðgjöf, greiningu eða meðferð, og ber enga ábyrgð á læknisfræðilegum
                    ákvörðunum sem teknar eru með stuðningi þjónustunnar.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">5.2. Ábyrgð á efni og úrvinnslu</h4>
                  <p>
                    Allt efni sem sent er í gegnum þjónustuna og allar ákvarðanir sem teknar eru á grundvelli þeirra eru
                    á ábyrgð notandans eða þess heilbrigðisstarfsmanns sem tekur upp eða vinnur úr upptökunum. Careflux
                    ber enga ábyrgð á að upplýsingarnar hafi gildi, séu réttar eða heilar né á þeirri úrvinnslu sem fer
                    fram af hálfu heilbrigðisstarfsfólks.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">5.3. Takmörkun ábyrgðar</h4>
                  <p>Careflux ber ekki ábyrgð á:</p>
                  <ul className="mt-2 ml-6 list-disc space-y-1">
                    <li>Ónákvæmum upplýsingum frá heilbrigðisstarfsfólki eða þriðja aðila.</li>
                    <li>Tímabundnu eða varanlegu láti á þjónustu heilbrigðisstofnana.</li>
                    <li>Skaða sem hlýst vegna vanhæfni til þess að nota þjónustuna.</li>
                    <li>Tæknilegum bilunum, nettruflunum, eða hugbúnaðarvillum hjá þriðja aðila.</li>
                  </ul>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">5.4. Ábyrgð heilbrigðisstarfsmanns sem notanda</h4>
                  <p>Notandi ber ábyrgð á:</p>
                  <ul className="mt-2 ml-6 list-disc space-y-1">
                    <li>Réttleika og gildi þeirra upplýsinga sem hann veitir.</li>
                    <li>Að nota þjónustuna í samræmi við lög, reglur og skilmála.</li>
                    <li>Að vernda aðgangsupplýsingar og tilkynna um grunsamlega eða óheimila notkun.</li>
                    <li>
                      Að lesa yfir og yfirfara þær upplýsingar sem Skríban leggur til áður en þær eru unnar frekar.
                    </li>
                  </ul>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">5.5. Undanþága frá skaðabótaskyldu</h4>
                  <p>
                    Careflux ber ekki skaðabótaskyldu gagnvart heilbrigðisþjónustuveitandanum og starfsfólki sem tekur
                    upp eða vinnur úr samtölum nema sýnt sé fram á stórfellt gáleysi við útfærslu öryggisatriða
                    hugbúnaðarins. Í öllum tilvikum takmarkast skaðabótaábyrgð Careflux við þá fjárhæð sem samsvarar
                    beinu tjóni og að hámarki einu ári af þjónustugjaldi, ef við á.
                  </p>
                </div>

                <div>
                  <h3 className="mb-3 font-bold text-xl">6. Breytingar á skilmálum</h3>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">6.1. Réttur til breytinga</h4>
                  <p>
                    Careflux áskilur sér rétt til að breyta, bæta við eða fjarlægja ákvæði í þessum skilmálum hvenær sem
                    er, að hluta eða í heild, ef nauðsyn krefur vegna lagabreytinga, tæknilegra breytinga á þjónustunni,
                    eða annarra málefnalegra ástæðna.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">6.2. Birting og tilkynning</h4>
                  <p>
                    Allar breytingar á skilmálunum verða birtar á heimasíðu þjónustunnar eða innan notendaviðmótsins. Í
                    tilvikum þar sem breytingar eru verulegar, verður tilkynning send með tölvupósti eða öðrum hætti sem
                    Careflux telur viðeigandi. Notandi getur ávallt nálgast gildandi skilmála á vefsíðu Careflux og í
                    notendaviðmóti.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">6.3. Gildistaka breytinga</h4>
                  <p>
                    Breytingar á skilmálum taka gildi við birtingu þeirra, nema annað sé sérstaklega tekið fram. Með
                    áframhaldandi notkun á þjónustunni eftir slíkar breytingar lýsir þú yfir samþykki þínu fyrir
                    uppfærðum skilmálum.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">6.4. Hafi notandi athugasemdir</h4>
                  <p>
                    Ef þú ert ósammála uppfærðum skilmálum getur þú hætt notkun þjónustunnar. Notkun þjónustunnar eftir
                    gildistöku breytinga jafngildir samþykki þeirra.
                  </p>
                </div>

                <div>
                  <h3 className="mb-3 font-bold text-xl">7. Lög og úrlausn ágreinings</h3>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">7.1. Gildandi lög</h4>
                  <p>Þessir skilmálar og öll notkun þín á þjónustunni lúta íslenskum lögum.</p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">7.2. Varnarþing og meðferð ágreinings</h4>
                  <p>
                    Rísi ágreiningur milli þín og Careflux um skilmálana eða þjónustuna almennt, skal leitast við að
                    leysa slíkan ágreining með samkomulagi. Takist það ekki innan sanngjarns frests, skal mál höfðað
                    fyrir Héraðsdómi Reykjavíkur, nema lög kveði á um annað.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">7.3. Ógild ákvæði</h4>
                  <p>
                    Ef eitthvert ákvæði þessara skilmála reynist ólögmætt, ógilt eða óframkvæmanlegt, hefur það ekki
                    áhrif á gildi eða framfylgni annarra ákvæða. Slíkt ákvæði skal skýrt, eftir því sem mögulegt er, í
                    samræmi við upphaflegan tilgang þess.
                  </p>
                </div>

                <div className="mt-8 rounded-lg bg-muted p-6">
                  <p className="mb-2">
                    <strong>Ef þú hefur spurningar varðandi þessa skilmála, hafðu samband við:</strong>
                  </p>
                  <p className="mb-1">
                    <strong>Careflux ehf.</strong>
                  </p>
                  <p>hello@careflux.ai</p>
                </div>

                <p className="mt-6 text-center text-muted-foreground text-sm">Síðast uppfært: 10. júlí 2025</p>
              </div>
            </>
          ) : (
            // English Version
            <>
              <h1 className="mb-6 text-center font-bold text-3xl">Terms and Conditions</h1>
              <h2 className="mb-8 text-center font-bold text-2xl">Terms of Use for the Scribe</h2>

              <div className="space-y-6 text-foreground">
                <div>
                  <p>
                    These Terms of Use ("Terms") apply to your access to and use of the software service that
                    transcribes and generates automatic summaries of clinical interviews, provided by{' '}
                    <strong>Careflux ehf.</strong>, kt. 490224-1140, located at Menntavegur 1, 102 Reykjavík, as well as
                    all associated services and systems collectively referred to as "Scribe" or "the service."
                  </p>
                  <p className="mt-4">
                    By using the service, you agree to these Terms and commit to complying with them.
                  </p>
                </div>

                <div>
                  <h3 className="mb-3 font-bold text-xl">1. Purpose of the Service</h3>
                  <p>
                    Scribe is software that records conversations between healthcare professionals and their clients and
                    automatically creates summaries and notes based on them. Scribe can also be used by the healthcare
                    professional without the patient to dictate notes before or after the clinical encounter and get a
                    summary. This enables healthcare professionals to better focus on their clients during
                    consultations. Scribe consists of: (1) a secure web portal where healthcare professionals log in;
                    and (2) software that uses AI models to automatically analyze the content of conversations—that is,
                    it transcribes the audio and generates summaries for the healthcare professional to review, approve,
                    or edit as needed.
                  </p>
                  <p className="mt-4">
                    These Terms of Use apply to healthcare professionals using this service to obtain transcription,
                    processing, and summaries of conversations with clients.
                  </p>
                  <p className="mt-4">
                    The service connects to medical records in a certified and secure manner, with data storage carried
                    out in accordance with applicable laws.
                  </p>
                  <p className="mt-4">
                    <strong>The service does not replace medical diagnosis or advice.</strong>
                  </p>
                </div>

                <div>
                  <h3 className="mb-3 font-bold text-xl">2. Conditions of Use</h3>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">2.1. Age and Eligibility</h4>
                  <p>
                    The service is intended for healthcare professionals who are 18 years or older and employed by a
                    recognized healthcare provider.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">2.2. Authentication and Access</h4>
                  <p>
                    Access to the service requires electronic identification using digital certificates, in accordance
                    with the requirements of the healthcare provider where the healthcare professional is employed. Such
                    identification is essential to ensure the security and legality of data processing.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">2.3. Lawful Use</h4>
                  <p>By using the service, you confirm that:</p>
                  <ul className="mt-2 ml-6 list-disc space-y-1">
                    <li>You are the person you claim to be.</li>
                    <li>
                      You use the service solely for lawful purposes and within the intended scope of the service.
                    </li>
                  </ul>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">2.4. Changes to Access</h4>
                  <p>Careflux reserves the right to:</p>
                  <ul className="mt-2 ml-6 list-disc space-y-1">
                    <li>
                      Temporarily or permanently restrict or block access to the service without notice if there is
                      suspicion of violations of the Terms, misuse, false information, or unlawful behavior.
                    </li>
                    <li>Grant or revoke access to the service at the request of the healthcare provider using it.</li>
                  </ul>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">2.5. Technical Requirements</h4>
                  <p>
                    To use the service, a healthcare professional must have electronic ID credentials, a microphone,
                    access to an internet-connected computer or mobile device, and use a web browser that supports the
                    service's security standards. The user is responsible for ensuring that their equipment and internet
                    connection meet these requirements.
                  </p>
                </div>

                <div>
                  <h3 className="mb-3 font-bold text-xl">3. Use of the Service</h3>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">3.1. Purpose of the Service</h4>
                  <p>
                    The service is a platform for communication between individuals and healthcare professionals where
                    conversations can be recorded and securely summarized.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">3.2. Service Functionality</h4>
                  <p>
                    The service records conversations or dictation, automatically processes their content, and generates
                    summaries for healthcare professionals. The healthcare professional reviews this data and is
                    responsible for the final decision and processing.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">3.3. Prohibited Use</h4>
                  <p>It is prohibited to:</p>
                  <ul className="mt-2 ml-6 list-disc space-y-1">
                    <li>Alter summaries in a misleading, inappropriate, or unlawful manner.</li>
                    <li>Use the service to harm, threaten, harass, or deceive others.</li>
                    <li>
                      Send content that violates laws or third-party rights, such as copyright, privacy, or data
                      protection.
                    </li>
                    <li>Use the service for automated purposes or through software that mimics a human.</li>
                    <li>
                      Use the service to process conversations indicating emergencies. In such cases, the individual
                      should be advised to call 112 and immediate appropriate action should be taken.
                    </li>
                  </ul>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">3.4. Handling of Conversations</h4>
                  <p>
                    Careflux staff do not review the content of recordings and are not responsible for when or how
                    healthcare professionals process them. Usage and processing are subject to the rules and procedures
                    of the respective healthcare institution.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">3.5. Information and User Responsibility</h4>
                  <p>
                    Healthcare professionals using the service are responsible for providing accurate and clear
                    information. Providing incorrect information may affect the processing of conversations, causing
                    errors, delays, or harm.
                  </p>
                  <p className="mt-4">
                    Healthcare professionals who use Scribe for dictation and have checked "Record without personally
                    identifiable information" must omit personally identifiable details from the dictation. This
                    primarily includes the individual's full name, national ID number, address, and exact workplace.
                  </p>
                  <p className="mt-4">
                    Scribe uses an AI model that may produce errors. By agreeing to these Terms, you acknowledge that it
                    is your responsibility as a healthcare professional to review and verify the information generated
                    by Scribe before further processing.
                  </p>
                </div>

                <div>
                  <h3 className="mb-3 font-bold text-xl">4. Privacy and Data Security</h3>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">4.1. Processing of Personal Data</h4>
                  <p>
                    Careflux processes personal data in accordance with the Act on Data Protection and the Processing of
                    Personal Data No. 90/2018 and Regulation (EU) 2016/679 (GDPR). Information that users send through
                    the service may be considered sensitive personal data, such as health information, and is handled
                    according to the strictest security standards.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">4.2. Roles and Responsibility</h4>
                  <p>
                    Careflux acts as a data processor on behalf of the relevant healthcare institution or service
                    provider, which is the data controller responsible for the information. All data storage and
                    processing is carried out under the authority and according to the instructions of the respective
                    healthcare provider.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">4.3. Data Transfer</h4>
                  <p>
                    Data sent through Scribe is encrypted during transmission and stored in compliance with security
                    requirements in cloud solutions and/or by the relevant healthcare service provider. Careflux does
                    not transfer data to third parties except with explicit consent, legal obligation, or an agreement
                    with the data controller.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">4.4. Your Rights as a Data Subject</h4>
                  <p>As a healthcare professional and user of Scribe, you have certain legal rights:</p>
                  <ul className="mt-2 ml-6 list-disc space-y-1">
                    <li>The right to access collected information</li>
                    <li>The right to correct inaccurate information</li>
                    <li>The right to restrict or object to processing</li>
                    <li>The right to have your data deleted</li>
                    <li>The right to withdraw your consent</li>
                    <li>The right to file a complaint with the Data Protection Authority</li>
                  </ul>
                  <p className="mt-4">
                    Such requests should be directed to the relevant healthcare service provider responsible for data
                    processing or to Careflux's Data Protection Officer at hello@careflux.ai.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">4.5. Security Measures</h4>
                  <p>
                    Scribe implements technical and organizational security measures to protect data against
                    unauthorized access, loss, deletion, or misuse. These measures include encryption, access control,
                    security testing, and regular security monitoring.
                  </p>
                </div>

                <div>
                  <h3 className="mb-3 font-bold text-xl">5. Careflux's Responsibility</h3>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">5.1. Technical Service Provider</h4>
                  <p>
                    Careflux provides only the technical solution for the service. Careflux does not provide medical or
                    healthcare services, advice, diagnosis, or treatment, and is not responsible for any medical
                    decisions made using the service.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">5.2. Responsibility for Content and Processing</h4>
                  <p>
                    All content sent through the service and all decisions made based on it are the responsibility of
                    the user or the healthcare professional who records or processes the recordings. Careflux bears no
                    responsibility for the validity, accuracy, or completeness of the information, nor for the
                    processing carried out by healthcare professionals.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">5.3. Limitation of Liability</h4>
                  <p>Careflux is not liable for:</p>
                  <ul className="mt-2 ml-6 list-disc space-y-1">
                    <li>Inaccurate information from healthcare professionals or third parties</li>
                    <li>Temporary or permanent outages of healthcare institutions' services</li>
                    <li>Damage resulting from inability to use the service</li>
                    <li>Technical failures, network disruptions, or software errors caused by third parties</li>
                  </ul>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">
                    5.4. Responsibility of the Healthcare Professional as User
                  </h4>
                  <p>The user is responsible for:</p>
                  <ul className="mt-2 ml-6 list-disc space-y-1">
                    <li>The accuracy and validity of the information they provide</li>
                    <li>Using the service in compliance with laws, regulations, and these Terms</li>
                    <li>Protecting access credentials and reporting any suspicious or unauthorized use</li>
                    <li>Reviewing and verifying the information generated by Scribe before any further processing</li>
                  </ul>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">5.5. Exemption from Liability for Damages</h4>
                  <p>
                    Careflux is not liable for damages to the healthcare service provider or staff who record or process
                    conversations unless gross negligence in implementing the software's security measures is proven. In
                    all cases, Careflux's liability is limited to direct damages and capped at an amount equal to one
                    year's service fee, if applicable.
                  </p>
                </div>

                <div>
                  <h3 className="mb-3 font-bold text-xl">6. Changes to the Terms</h3>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">6.1. Right to Amend</h4>
                  <p>
                    Careflux reserves the right to modify, add to, or remove provisions in these Terms at any time, in
                    whole or in part, as necessary due to legal changes, technical updates to the service, or other
                    valid reasons.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">6.2. Publication and Notification</h4>
                  <p>
                    All changes to the Terms will be published on the service's website or within the user interface. In
                    cases of significant changes, a notification will be sent by email or by other means deemed
                    appropriate by Careflux. Users can always access the current Terms on the Careflux website and
                    within the user interface.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">6.3. Effective Date of Changes</h4>
                  <p>
                    Changes to the Terms take effect upon publication, unless otherwise specifically stated. Continued
                    use of the service after such changes indicates your acceptance of the updated Terms.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">6.4. User Objections</h4>
                  <p>
                    If you disagree with the updated Terms, you may stop using the service. Continued use of the service
                    after the changes take effect constitutes acceptance of those changes.
                  </p>
                </div>

                <div>
                  <h3 className="mb-3 font-bold text-xl">7. Law and Dispute Resolution</h3>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">7.1. Governing Law</h4>
                  <p>These Terms and all your use of the service are governed by Icelandic law.</p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">7.2. Jurisdiction and Dispute Resolution</h4>
                  <p>
                    If a dispute arises between you and Careflux regarding the Terms or the service generally, efforts
                    should be made to resolve it amicably. If no resolution is reached within a reasonable time, the
                    dispute shall be brought before the District Court of Reykjavík, unless otherwise required by law.
                  </p>
                  <h4 className="mt-4 mb-2 font-semibold text-lg">7.3. Severability</h4>
                  <p>
                    If any provision of these Terms is found to be unlawful, invalid, or unenforceable, it shall not
                    affect the validity or enforcement of the remaining provisions. Such a provision shall be
                    interpreted, as far as possible, in accordance with its original intent.
                  </p>
                </div>

                <div className="mt-8 rounded-lg bg-muted p-6">
                  <p className="mb-2">
                    <strong>If you have any questions regarding these Terms, please contact:</strong>
                  </p>
                  <p className="mb-1">
                    <strong>Careflux ehf.</strong>
                  </p>
                  <p>hello@careflux.ai</p>
                </div>

                <p className="mt-6 text-center text-muted-foreground text-sm">Last updated: July 10, 2025</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
